import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { map, Observable, of, switchMap, tap } from 'rxjs';

import {
	AccountConfig,
	ContactConfig,
	ContactDTO,
	ContactPerson,
	DEFAULT_TRADER_LIFECYCLE,
	FundConfig,
	FundContactConfig,
	StrategyConfig,
	StrategySignalConnectionConfig,
	SymbolConfig,
	TraderConfig
} from '@demeter/lib/models/config';
import { SignalConfig } from '@demeter/lib/models/signal';
import { SystemSettingConfig } from '@demeter/lib/models/strategy-ledger';
import { MarketHoursBundle, MarketHoursConfig } from '@demeter/lib/schedule/schedule.models';

import { environment } from '../../environments/environment';
import { AuthService } from '../auth';

export interface QueryParams {
	[param: string]: string | number | boolean;
}

// See DEFAULT_TRADER_LIFECYCLE: a daily-aggregate strategy arrives with no `lifecycle` key at
// all. Applied to reads AND to the save response, which is patched straight into the strategies
// signal cache and would otherwise put the '?' back on a row that just rendered correctly.
const withDefaultLifecycle = (s: StrategyConfig): StrategyConfig => ({
	...s,
	lifecycle: s.lifecycle || DEFAULT_TRADER_LIFECYCLE
});

// Contact/fund-contact wire shapes. Unlike funds/accounts (default camelCase JSON), the
// backend ContactDTO/ContactPersonDTO pin PascalCase keys via [JsonPropertyName] — map at
// this boundary only, everywhere else in the app stays camelCase.
interface ContactWire {
	ContactId: string;
	Name: string;
	Email: string;
	Phone: string | null;
}

interface ContactDTOWire extends ContactWire {
	FundId: string;
	BrokerId: string | null;
	CanAuthorize: boolean;
	ReceivesReports: boolean;
}

interface FundContactWire {
	FundId: string;
	ContactId: string;
	BrokerId: string;
	CanAuthorize: boolean;
	ReceivesReports: boolean;
}

function toContactWire(c: ContactConfig): ContactWire {
	return {
		ContactId: c.contactId,
		Name: c.name,
		Email: c.email,
		Phone: c.phone ? c.phone : null
	};
}

function fromContactWire(w: ContactWire): ContactConfig {
	return {
		contactId: w.ContactId,
		name: w.Name,
		email: w.Email,
		phone: w.Phone ? w.Phone : undefined
	};
}

function fromContactDTOWire(w: ContactDTOWire): ContactDTO {
	return {
		contactId: w.ContactId,
		name: w.Name,
		email: w.Email,
		phone: w.Phone ? w.Phone : undefined,
		fundId: w.FundId,
		brokerId: w.BrokerId ?? null,
		canAuthorize: w.CanAuthorize,
		receivesReports: w.ReceivesReports
	};
}

function fromContactPersonWire(w: ContactWire): ContactPerson {
	return {
		contactId: w.ContactId,
		name: w.Name,
		email: w.Email,
		phone: w.Phone ? w.Phone : undefined
	};
}

function toFundContactWire(l: FundContactConfig): FundContactWire {
	return {
		FundId: l.fundId,
		ContactId: l.contactId,
		BrokerId: l.brokerId ? l.brokerId : '',
		CanAuthorize: l.canAuthorize,
		ReceivesReports: l.receivesReports
	};
}

function fromFundContactWire(w: FundContactWire): FundContactConfig {
	return {
		fundId: w.FundId,
		contactId: w.ContactId,
		brokerId: w.BrokerId ? w.BrokerId : '',
		canAuthorize: w.CanAuthorize,
		receivesReports: w.ReceivesReports
	};
}

// FundContact partition key convention: bare contactId for a fund-wide link, otherwise
// `${contactId}:${brokerId}` for a broker-scoped one. Callers build the delete rowKey with this.
export function fundContactRowKey(contactId: string, brokerId: string): string {
	return brokerId ? `${contactId}:${brokerId}` : contactId;
}

@Injectable({
	providedIn: 'root'
})
export class CoreConfigApiService {
	private get ROOT_URL(): string {
		return `${environment.coreConfigUrl}`;
	}
	private accounts: WritableSignal<AccountConfig[]> = signal([]);
	private funds: WritableSignal<FundConfig[]> = signal([]);

	constructor(
		private httpClient: HttpClient,
		private authService: AuthService
	) {}

	public getAccounts(): Observable<AccountConfig[]> {
		if (this.accounts().length) {
			return of(this.accounts());
		}
		const url = `/accounts`;
		return this._get<AccountConfig[]>(url, {}).pipe(
			tap(accounts => {
				this.accounts.set(accounts);
			})
		);
	}

	public getFunds(): Observable<FundConfig[]> {
		if (this.funds().length) {
			return of(this.funds());
		}
		const url = `/funds`;
		return this._get<FundConfig[]>(url, {}).pipe(
			tap(funds => {
				this.funds.set(funds);
			})
		);
	}

	public getFund(fundId: string): Observable<FundConfig | undefined> {
		return this.getFunds().pipe(map(funds => funds.find(f => f.fundId === fundId)));
	}

	public postFund(fund: FundConfig): Observable<FundConfig> {
		const url = `/funds`;
		return this._post<FundConfig>(url, fund).pipe(
			tap(() => {
				this.funds.set([]);
			})
		);
	}

	public deleteFund(fundId: string): Observable<void> {
		const url = `/funds/${fundId}`;
		return this._delete(url, {}).pipe(
			tap(() => {
				this.funds.set([]);
			})
		);
	}

	// No persisted cache here (unlike getFunds/getAccounts): contact data is PII and must be
	// read live rather than served from a signal that can go stale.
	public getContacts(
		fundId?: string,
		canAuthorize?: boolean,
		receivesReports?: boolean
	): Observable<ContactDTO[]> {
		const url = `/contacts`;
		const params: QueryParams = {};
		if (fundId !== undefined) {
			params['fundId'] = fundId;
		}
		if (canAuthorize !== undefined) {
			params['canAuthorize'] = canAuthorize;
		}
		if (receivesReports !== undefined) {
			params['receivesReports'] = receivesReports;
		}
		return this._get<ContactDTOWire[]>(url, params).pipe(
			map(wire => wire.map(fromContactDTOWire))
		);
	}

	public getAllContacts(): Observable<ContactPerson[]> {
		const url = `/contacts/all`;
		return this._get<ContactWire[]>(url, {}).pipe(map(wire => wire.map(fromContactPersonWire)));
	}

	public saveContact(contact: ContactConfig): Observable<ContactConfig> {
		const url = `/contacts`;
		return this._post<ContactWire>(url, toContactWire(contact)).pipe(map(fromContactWire));
	}

	public deleteContact(contactId: string): Observable<void> {
		const url = `/contacts/${encodeURIComponent(contactId)}`;
		return this._delete(url, {});
	}

	public saveFundContact(link: FundContactConfig): Observable<FundContactConfig> {
		const url = `/fund-contacts`;
		return this._post<FundContactWire>(url, toFundContactWire(link)).pipe(
			map(fromFundContactWire)
		);
	}

	public deleteFundContact(fundId: string, rowKey: string): Observable<void> {
		const url = `/fund-contacts/${encodeURIComponent(fundId)}/${encodeURIComponent(rowKey)}`;
		return this._delete(url, {});
	}

	public getTraders(): Observable<TraderConfig[]> {
		const url = `/traders`;
		return this._get<TraderConfig[]>(url, {});
	}

	public getStrategies(): Observable<StrategyConfig[]> {
		const url = `/strategy`;
		return this._get<StrategyConfig[]>(url, {}).pipe(
			map(strategies => strategies.map(withDefaultLifecycle))
		);
	}

	// Core.Config GET /settings (ADMIN). A 200 [] is "no row"; the caller maps it.
	public getSystemSettings(): Observable<SystemSettingConfig[]> {
		return this._get<SystemSettingConfig[]>(`/settings`, {});
	}

	public getSignals(): Observable<SignalConfig[]> {
		const url = `/signals`;
		return this._get<SignalConfig[]>(url, {});
	}

	// The server answers 200 with a literal `null` body when neither a per-date nor a DEFAULT
	// row exists — that is a meaningful business state ("calendar unavailable"), not an error.
	// Never coerce it to undefined, and never default a null result to isTradingDay: false.
	public getMarketHours(venue: string, date: string): Observable<MarketHoursConfig | null> {
		return this._get<MarketHoursConfig | null>('/market-hours', { venue, date });
	}

	public getMarketHoursAll(date: string): Observable<MarketHoursBundle> {
		return this._get<MarketHoursBundle>('/market-hours/all', { date });
	}

	public getSymbols(): Observable<SymbolConfig[]> {
		const url = `/symbols`;
		return this._get<SymbolConfig[]>(url, {});
	}

	public saveSymbol(symbolConfig: any): Observable<SymbolConfig> {
		const url = `/symbols`;
		return this._post<SymbolConfig>(url, symbolConfig);
	}

	public deleteSymbol(symbol: string): Observable<void> {
		const url = `/symbols/${symbol}`;
		return this._delete(url, {});
	}

	public getStrategySignalConnections(): Observable<StrategySignalConnectionConfig[]> {
		const url = `/strategy-signal-connection`;
		return this._get<StrategySignalConnectionConfig[]>(url, {});
	}

	public saveAccount(accountConfig: any): Observable<AccountConfig> {
		const url = `/accounts`;
		return this._post<AccountConfig>(url, accountConfig).pipe(
			tap(() => {
				this.accounts.set([]);
			})
		);
	}

	public saveTrader(traderConfig: any): Observable<TraderConfig> {
		const url = `/traders`;
		return this._post<TraderConfig>(url, traderConfig);
	}

	public saveSignal(signalConfig: any): Observable<SignalConfig> {
		const url = `/signals`;
		return this._post<SignalConfig>(url, signalConfig);
	}

	public saveStrategy(strategyConfig: any): Observable<StrategyConfig> {
		const url = `/strategy`;
		return this._post<StrategyConfig>(url, strategyConfig).pipe(map(withDefaultLifecycle));
	}

	public deleteAccount(brokerAccountId: string, brokerId: string): Observable<void> {
		// The path segment is the BrokerAccountId; EZE ids contain ';' so encoding
		// is load-bearing, not cosmetic.
		const url = `/accounts/${encodeURIComponent(brokerAccountId)}`;
		return this._delete(url, { brokerId }).pipe(
			tap(() => {
				this.accounts.set([]);
			})
		);
	}

	public deleteTrader(
		accountId: string,
		strategyId: string,
		brokerAccountId: string
	): Observable<void> {
		const url = `/traders/${accountId}`;
		return this._delete(url, { strategyId, brokerAccountId });
	}

	public deleteSignal(signalId: string): Observable<void> {
		const url = `/signals/${signalId}`;
		return this._delete(url, {});
	}

	public deleteStrategies(strategyId: string, signalId: string): Observable<void> {
		const url = `/strategy/${strategyId}`;
		return this._delete(url, { signalId });
	}

	public saveStrategySignalConnection(
		connection: any
	): Observable<StrategySignalConnectionConfig> {
		const url = `/strategy-signal-connection`;
		return this._post<StrategySignalConnectionConfig>(url, connection);
	}

	public deleteStrategySignalConnection(strategyId: string, signalId: string): Observable<void> {
		const url = `/strategy-signal-connection/${encodeURIComponent(strategyId)}/${encodeURIComponent(signalId)}`;
		return this._delete(url, {});
	}

	private _get<T>(url: string, queryParams: QueryParams): Observable<T> {
		return this.authService.getAccessToken().pipe(
			switchMap(token => {
				return this.httpClient.get<T>(`${this.ROOT_URL}${url}`, {
					headers: { Authorization: `Bearer ${token}` },
					params: new HttpParams({ fromObject: queryParams as Record<string, string> })
				});
			})
		);
	}

	private _post<T>(url: string, body: any): Observable<T> {
		return this.authService.getAccessToken().pipe(
			switchMap(token => {
				return this.httpClient.post<T>(`${this.ROOT_URL}${url}`, body, {
					headers: { Authorization: `Bearer ${token}` }
				});
			})
		);
	}

	private _put<T>(url: string, body: any, queryParams: QueryParams): Observable<T> {
		return this.authService.getAccessToken().pipe(
			switchMap(token => {
				return this.httpClient.put<T>(`${this.ROOT_URL}${url}`, body, {
					headers: { Authorization: `Bearer ${token}` },
					params: new HttpParams({ fromObject: queryParams as Record<string, string> })
				});
			})
		);
	}

	private _delete(url: string, queryParams: QueryParams): Observable<void> {
		return this.authService.getAccessToken().pipe(
			switchMap(token => {
				return this.httpClient.delete(`${this.ROOT_URL}${url}`, {
					headers: { Authorization: `Bearer ${token}` },
					params: new HttpParams({ fromObject: queryParams as Record<string, string> })
				});
			}),
			map(() => {})
		);
	}
}
